import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { selectValidRevisionEntities } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setDidHierarchySaveFail,
  setHierarchySaveTargetId,
  setIsSavingHierarchyChanges,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectRegistrationRevision,
  selectRevisionEntities,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { setEntities } from "@/data-preparation-tool/store/revision/revision-slice";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { FaroText, SortDownIcon } from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntityRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { ListItemIcon, MenuItem } from "@mui/material";
import { JSX, useCallback } from "react";
import {
  applySortedDescendants,
  collectSortedDescendants,
  saveSortedEntities,
} from "../scan-tree-sort-utils";

type SortDescendantsContextMenuActionItemProps = {
  /** The entity whose descendants are sorted. */
  entity: CaptureTreeEntityRevision;
  /** Whether the context menu action item should be disabled. */
  disabled?: boolean;
  /** Callback executed when the sorting was triggered. */
  onSortStarted(): void;
};

/**
 * @returns A context menu item to sort all descendants of an entity by name,
 *  with the clusters always before the scans.
 */
export function SortDescendantsContextMenuActionItem({
  entity,
  disabled,
  onSortStarted,
}: SortDescendantsContextMenuActionItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const onSort = useCallback(async () => {
    const state = getState();
    const revision = selectRegistrationRevision(state);
    const sortedDescendants = collectSortedDescendants(
      selectValidRevisionEntities(state),
      entity.id,
    );

    // Close the context menu right away: the new order is shown immediately and the progress of
    // saving it is reported by the draft saving indicator.
    onSortStarted();
    if (!revision || !sortedDescendants.length) {
      return;
    }

    // Show the new order in the tree and 3D view right away, before the backend confirmed it.
    const previousEntities = selectRevisionEntities(state);
    dispatch(setIsSavingHierarchyChanges(true));
    dispatch(setHierarchySaveTargetId(entity.id));
    dispatch(setDidHierarchySaveFail(false));
    dispatch(
      setEntities(applySortedDescendants(previousEntities, sortedDescendants)),
    );

    try {
      try {
        await saveSortedEntities({
          projectApiClient,
          state,
          revision,
          sortedDescendants,
        });
      } catch (error: unknown) {
        // Roll back the optimistic update
        dispatch(setEntities(previousEntities));
        dispatch(setDidHierarchySaveFail(true));
        handleErrorWithToast({
          error,
          title:
            "Failed to save the new order. Please reload the page and try again.",
        });
        return;
      }

      try {
        await dispatch(loadRevisionDataThunk({ projectApiClient }));
      } catch (error: unknown) {
        handleErrorWithToast({
          error,
          title: "Failed to load updated data. Please reload the page.",
        });
      }
    } finally {
      dispatch(setHierarchySaveTargetId(null));
      dispatch(setIsSavingHierarchyChanges(false));
    }
  }, [
    dispatch,
    entity.id,
    getState,
    handleErrorWithToast,
    onSortStarted,
    projectApiClient,
  ]);

  return (
    <MenuItem key="sortDescendants" onClick={onSort} disabled={disabled}>
      <ListItemIcon>
        <SortDownIcon />
      </ListItemIcon>
      <FaroText variant="labelL">Sort</FaroText>
    </MenuItem>
  );
}
